import { ConversationStatus } from '@provision/conversation-types';
import dayjs from 'dayjs';
import { useCallback } from 'react';
import { useNotifications } from 'src/utils/hooks';
import { useSnoozeConversation } from './conversation';
import { Conversation } from './conversation.types';
import { useConversationUpdate } from './use-conversation-update';

const SNOOZE_FAILED_MESSAGE =
  'Something went wrong snoozing the conversation, please try again';

type SnoozeUntil = Partial<Pick<Conversation, 'snoozeUntil'>>;

export type ConversationStatusSnapshot = Pick<
  Conversation,
  'conversationId' | 'conversationStatusId'
> &
  SnoozeUntil;

export const canRestoreSnooze = ({ snoozeUntil }: SnoozeUntil) =>
  !!snoozeUntil && dayjs(snoozeUntil).isAfter(dayjs());

export const useRestoreConversationStatus = () => {
  const { snoozeConversation } = useSnoozeConversation();
  const { updateConversation } = useConversationUpdate();
  const { showErrorAlert } = useNotifications();

  return useCallback(
    async ({
      conversationId,
      conversationStatusId,
      snoozeUntil,
    }: ConversationStatusSnapshot) => {
      if (
        conversationStatusId === (ConversationStatus.Snoozed as string) &&
        snoozeUntil &&
        canRestoreSnooze({ snoozeUntil })
      ) {
        const response = await snoozeConversation({ conversationId, snoozeUntil });

        // an undefined response was already reported by the hook's onError
        if (response && !response.success) {
          showErrorAlert(response.error ?? SNOOZE_FAILED_MESSAGE);
        }
        return;
      }

      // only the status goes in: the hook writes every field it is given back to the cache
      await updateConversation({ conversationId, conversationStatusId } as Conversation);
    },
    [showErrorAlert, snoozeConversation, updateConversation],
  );
};
